"use client";

import type React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { apiRemote, IParticipants } from "@/api";
import { TableSortLabel } from "@mui/material";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const renderStatus = (status: string | null) => {
  let label = "N/A";
  let color: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" = "default";

  switch (status) {
    case "submitted":
      label = "Submitted";
      color = "success"; // xanh lá
      break;
    case "unsubmitted":
      label = "Unsubmitted";
      color = "error"; // đỏ
      break;
    case "in_progress":
      label = "In progress";
      color = "warning"; // cam
      break;
  }

  return <Chip label={label} color={color} size="small" />;
};

export default function ParticipantList() {
  const [participants, setParticipants] = useState<IParticipants["data"] | []>(
    []
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalRows, setTotalRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [orderBy, setOrderBy] = useState<string>(''); // tên cột
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('asc'); // hướng sắp xếp

  const fetchParticipants = async (searchTerm? : string) => {
    setLoading(true);
    try {
      const res = await apiRemote.getParticipants(page + 1, rowsPerPage, searchTerm, orderBy, orderDirection); // +1 vì page server thường bắt đầu từ 1
      // console.log(res);
      setParticipants(res.data || []);
      setTotalRows(res.count || 0);
    } catch (error) {
      console.error("Failed to fetch participants:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParticipants(searchTerm);
  }, [page, rowsPerPage, orderBy, orderDirection]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    fetchParticipants(searchTerm);
    // Optional: nếu API có hỗ trợ search => fetchParticipants()
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSort = (column: string) => {
    if (orderBy === column) {
      setOrderDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(column);
      setOrderDirection('asc');
    }
  };

  const handleExportExcel = async () => {
    try {
      // setLoading(true);
      // const res = await apiRemote.getParticipants(undefined, undefined, searchTerm, orderBy, orderDirection);
      // const data = res.data || [];
  
      if (participants.length === 0) {
        alert("No data to export");
        return;
      }
  
      // Chuyển đổi dữ liệu
      const formattedData = participants.map((item: any) => ({
        'Full Name': item.display_name,
        'Username': item.user_login,
        'Email': item.user_email,
        'Country': item.countryName,
        'Created At': item.created_at,
        'Status': item.status,
      }));
  
      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Participants");
  
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
  
      const fileName = `participants_${new Date().toISOString()}.xlsx`;
      const dataBlob = new Blob([excelBuffer], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
  
      saveAs(dataBlob, fileName);
    } catch (error) {
      console.error("Export error:", error);
    } finally {
      // setLoading(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Participant List</Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search participant (name, email, country)..."
            value={searchTerm}
            onChange={handleChange}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />,
            }}
          />
          <Button variant="contained" onClick={handleSearch}>
            Search
          </Button>

          <Button variant="outlined" onClick={handleExportExcel}>
            Export to Excel
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="participant table">
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'display_name' ? orderDirection : false}>
                <TableSortLabel
                  active={orderBy === 'display_name'}
                  direction={orderBy === 'display_name' ? orderDirection : 'asc'}
                  onClick={() => handleSort('display_name')}
                >
                  Full Name
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'user_login' ? orderDirection : false}>
                <TableSortLabel
                  active={orderBy === 'user_login'}
                  direction={orderBy === 'user_login' ? orderDirection : 'asc'}
                  onClick={() => handleSort('user_login')}
                >
                  Username
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'user_email' ? orderDirection : false}>
                <TableSortLabel
                  active={orderBy === 'user_email'}
                  direction={orderBy === 'user_email' ? orderDirection : 'asc'}
                  onClick={() => handleSort('user_email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'countryName' ? orderDirection : false}>
                <TableSortLabel
                  active={orderBy === 'countryName'}
                  direction={orderBy === 'countryName' ? orderDirection : 'asc'}
                  onClick={() => handleSort('countryName')}
                >
                  Country
                </TableSortLabel>
              </TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                    <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : participants.length > 0 ? (
              participants.map((participant) => (
                <TableRow key={participant.ID}>
                  <TableCell>
                    {participant.display_name}
                  </TableCell>
                  <TableCell>{participant.user_login}</TableCell>
                  <TableCell>{participant.user_email}</TableCell>
                  <TableCell>{participant.countryName}</TableCell>
                  <TableCell>{participant.created_at}</TableCell>
                  <TableCell>{renderStatus(participant.status)}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No participants found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100, 500]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
}
